/**
 * Atelier assists — consumable slice tools (inventory + apply helpers).
 * Catalog: docs/atelier-assists.md
 */

import { ENERGY_MAX, getEnergyState, refundEnergyLocal } from './progression';

const STORAGE_KEY = 'vq_assists_v1';
const LOCK_OIL_MS = 5 * 60 * 1000;

export const ASSIST = {
    VOID_STAMP: 'void_stamp',
    ERROR_DUST: 'error_dust',
    CHALK_TIP: 'chalk_tip',
    LOCK_OIL: 'lock_oil',
    SPARE_KEY: 'spare_key',
};

export const ASSIST_META = {
    [ASSIST.VOID_STAMP]: {
        id: ASSIST.VOID_STAMP,
        name: 'Void Stamp',
        blurb: 'Auto-MARK every empty cell on rows/columns with a 0 clue.',
    },
    [ASSIST.ERROR_DUST]: {
        id: ASSIST.ERROR_DUST,
        name: 'Error Dust',
        blurb: 'Highlights up to 3 wrong DRAW/MARK cells (not the whole board). Empty cells are ignored.',
    },
    [ASSIST.CHALK_TIP]: {
        id: ASSIST.CHALK_TIP,
        name: 'Chalk Tip',
        blurb: 'Picks one empty cell and applies the correct action — DRAW or MARK — for you.',
    },
    [ASSIST.LOCK_OIL]: {
        id: ASSIST.LOCK_OIL,
        name: 'Lock Oil',
        blurb:
            'Add +5 minutes to your claim timer. When time runs out the slice reopens for others — your carve draft is kept, but reclaiming costs another key.',
    },
    [ASSIST.SPARE_KEY]: {
        id: ASSIST.SPARE_KEY,
        name: 'Spare Key',
        blurb: 'Restore +1 energy key',
    },
};

const CELL = { EMPTY: 0, FILLED: 1, MARKED: 2 };

function today() {
    return new Date().toISOString().split('T')[0];
}

function defaultInventory() {
    return {
        [ASSIST.VOID_STAMP]: 1,
        [ASSIST.ERROR_DUST]: 1,
        [ASSIST.CHALK_TIP]: 1,
        [ASSIST.LOCK_OIL]: 1,
        [ASSIST.SPARE_KEY]: 1,
    };
}

function loadRaw() {
    try {
        const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        if (!raw || typeof raw !== 'object') {
            return {
                inventory: defaultInventory(),
                daily: { date: today() },
                flags: {},
                stats: { solves: 0 },
            };
        }
        return {
            inventory: { ...defaultInventory(), ...(raw.inventory || {}) },
            daily: raw.daily || { date: today() },
            flags: raw.flags || {},
            stats: raw.stats || { solves: 0 },
        };
    } catch {
        return {
            inventory: defaultInventory(),
            daily: { date: today() },
            flags: {},
            stats: { solves: 0 },
        };
    }
}

function saveRaw(state) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function getAssistInventory() {
    return { ...loadRaw().inventory };
}

export function getAssistCount(id) {
    return loadRaw().inventory[id] || 0;
}

export function grantAssist(id, amount = 1) {
    const state = loadRaw();
    state.inventory[id] = (state.inventory[id] || 0) + amount;
    saveRaw(state);
    return state.inventory[id];
}

export function spendAssist(id) {
    const state = loadRaw();
    const n = state.inventory[id] || 0;
    if (n <= 0) return { ok: false, count: 0 };
    state.inventory[id] = n - 1;
    saveRaw(state);
    return { ok: true, count: state.inventory[id] };
}

/** Daily Void Stamp + reset day flags when the date rolls. */
export function ensureDailyAssists() {
    const state = loadRaw();
    const d = today();
    if (state.daily?.date === d && state.daily?.voidGranted) {
        return getAssistInventory();
    }
    if (state.daily?.date !== d) {
        state.daily = { date: d };
        state.flags = {
            ...state.flags,
            firstSliceToday: false,
            halfVaultOil: false,
            completeKeyToday: false,
        };
    }
    if (!state.daily.voidGranted) {
        state.inventory[ASSIST.VOID_STAMP] = (state.inventory[ASSIST.VOID_STAMP] || 0) + 1;
        state.daily.voidGranted = true;
        state.daily.date = d;
        saveRaw(state);
    }
    return getAssistInventory();
}

/**
 * Call after a successful slice solve to drip assists.
 * @param {{ pctAfter: number, isFirstSliceToday?: boolean, tier?: string }} meta
 */
export function onSliceSolvedAssistDrops(meta = {}) {
    const state = loadRaw();
    state.stats.solves = (state.stats.solves || 0) + 1;
    const granted = [];
    const isAtelier = meta.tier === 'atelier';

    state.inventory[ASSIST.VOID_STAMP] = (state.inventory[ASSIST.VOID_STAMP] || 0) + 1;
    granted.push(ASSIST.VOID_STAMP);
    // Atelier: bonus Void Stamp ~40% of the time
    if (isAtelier && Math.random() < 0.4) {
        state.inventory[ASSIST.VOID_STAMP] = (state.inventory[ASSIST.VOID_STAMP] || 0) + 1;
        granted.push(ASSIST.VOID_STAMP);
    }

    if (state.stats.solves % 2 === 0 || (isAtelier && Math.random() < 0.55)) {
        state.inventory[ASSIST.ERROR_DUST] = (state.inventory[ASSIST.ERROR_DUST] || 0) + 1;
        granted.push(ASSIST.ERROR_DUST);
    }

    if (meta.isFirstSliceToday && !state.flags.firstSliceToday) {
        state.inventory[ASSIST.CHALK_TIP] = (state.inventory[ASSIST.CHALK_TIP] || 0) + 1;
        state.flags.firstSliceToday = true;
        granted.push(ASSIST.CHALK_TIP);
    } else if (isAtelier && Math.random() < 0.25) {
        state.inventory[ASSIST.CHALK_TIP] = (state.inventory[ASSIST.CHALK_TIP] || 0) + 1;
        granted.push(ASSIST.CHALK_TIP);
    }

    if (meta.pctAfter >= 50 && !state.flags.halfVaultOil) {
        state.inventory[ASSIST.LOCK_OIL] = (state.inventory[ASSIST.LOCK_OIL] || 0) + 1;
        state.flags.halfVaultOil = true;
        granted.push(ASSIST.LOCK_OIL);
    } else if (isAtelier && meta.pctAfter >= 50 && Math.random() < 0.2) {
        // Atelier-only bonus roll past the 50% threshold
        state.inventory[ASSIST.LOCK_OIL] = (state.inventory[ASSIST.LOCK_OIL] || 0) + 1;
        granted.push(ASSIST.LOCK_OIL);
    }

    if (meta.pctAfter >= 100 && !state.flags.completeKeyToday) {
        state.inventory[ASSIST.SPARE_KEY] = (state.inventory[ASSIST.SPARE_KEY] || 0) + 1;
        state.flags.completeKeyToday = true;
        granted.push(ASSIST.SPARE_KEY);
    } else if (isAtelier && meta.pctAfter >= 100 && Math.random() < 0.2) {
        state.inventory[ASSIST.SPARE_KEY] = (state.inventory[ASSIST.SPARE_KEY] || 0) + 1;
        granted.push(ASSIST.SPARE_KEY);
    }

    saveRaw(state);
    return { granted, inventory: { ...state.inventory } };
}

export function clueSum(clueLine) {
    if (!clueLine || !clueLine.length) return 0;
    if (clueLine.length === 1 && clueLine[0] === 0) return 0;
    return clueLine.reduce((a, b) => a + b, 0);
}

export function isZeroClue(clueLine) {
    return clueSum(clueLine) === 0;
}

/** A1 — mark vacant on all 0-clue lines. */
export function applyVoidStamp(grid, clues) {
    if (!grid?.length || !clues?.rows || !clues?.cols) {
        return { ok: false, error: 'NO_GRID' };
    }
    const next = grid.map((row) => [...row]);
    let marked = 0;
    const n = next.length;

    for (let r = 0; r < n; r++) {
        if (!isZeroClue(clues.rows[r])) continue;
        for (let c = 0; c < n; c++) {
            if (next[r][c] === CELL.EMPTY) {
                next[r][c] = CELL.MARKED;
                marked += 1;
            }
        }
    }
    for (let c = 0; c < n; c++) {
        if (!isZeroClue(clues.cols[c])) continue;
        for (let r = 0; r < n; r++) {
            if (next[r][c] === CELL.EMPTY) {
                next[r][c] = CELL.MARKED;
                marked += 1;
            }
        }
    }

    if (marked === 0) {
        return { ok: false, error: 'NOTHING_TO_MARK', grid: next, marked: 0 };
    }
    return { ok: true, grid: next, marked };
}

function longestFillRun(line) {
    let best = 0;
    let cur = 0;
    for (const v of line) {
        if (v === CELL.FILLED) {
            cur += 1;
            best = Math.max(best, cur);
        } else {
            cur = 0;
        }
    }
    return best;
}

function maxClueRun(clueLine) {
    if (!clueLine?.length || (clueLine.length === 1 && clueLine[0] === 0)) return 0;
    return Math.max(...clueLine);
}

/** Max cells Error Dust may flag — prevents “paint everything → silhouette” abuse. */
const ERROR_DUST_MAX = 3;

function shuffleInPlace(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = arr[i];
        arr[i] = arr[j];
        arr[j] = tmp;
    }
    return arr;
}

/**
 * Flag a few committed mistakes (capped). Never dumps the full wrong-set —
 * that would outline the solution if the player floods the board with DRAW or MARK.
 * @param {number[][]} grid
 * @param {{rows:number[][], cols:number[][]}} clues
 * @param {number[][]|null} solution optional claimer solution for precise wrong-cell detection
 * @returns {{ ok: boolean, highlights: Array<{r,c,kind}>, totalFound?: number, error?: string }}
 */
export function applyErrorDust(grid, clues, solution = null) {
    if (!grid?.length || !clues?.rows || !clues?.cols) {
        return { ok: false, error: 'NO_GRID', highlights: [] };
    }
    const candidates = [];
    const seen = new Set();
    const add = (r, c) => {
        const k = `${r},${c}`;
        if (seen.has(k)) return;
        seen.add(k);
        candidates.push({ r, c, kind: 'error' });
    };

    const n = grid.length;
    const hasSolution =
        Array.isArray(solution) &&
        solution.length === n &&
        Array.isArray(solution[0]) &&
        solution[0].length === n;

    // Precise: committed cells that disagree with the solution
    if (hasSolution) {
        for (let r = 0; r < n; r++) {
            for (let c = 0; c < n; c++) {
                const cur = grid[r][c];
                if (cur === CELL.EMPTY) continue;
                const wantFill = solution[r][c] === 1;
                if (cur === CELL.FILLED && !wantFill) add(r, c);
                if (cur === CELL.MARKED && wantFill) add(r, c);
            }
        }
    }

    // Clue overfills / zero-clue fills (works even without solution)
    for (let r = 0; r < n; r++) {
        const row = grid[r];
        const clue = clues.rows[r] || [0];
        const sum = clueSum(clue);
        const fills = row.filter((v) => v === CELL.FILLED).length;
        const maxRun = maxClueRun(clue);
        const long = longestFillRun(row);

        if (isZeroClue(clue)) {
            row.forEach((v, c) => {
                if (v === CELL.FILLED) add(r, c);
            });
            continue;
        }
        if (fills > sum) {
            row.forEach((v, c) => {
                if (v === CELL.FILLED) add(r, c);
            });
        }
        if (maxRun > 0 && long > maxRun) {
            let cur = 0;
            let start = 0;
            for (let c = 0; c <= n; c++) {
                const v = c < n ? row[c] : CELL.EMPTY;
                if (v === CELL.FILLED) {
                    if (cur === 0) start = c;
                    cur += 1;
                } else {
                    if (cur > maxRun) {
                        for (let x = start; x < start + cur; x++) add(r, x);
                    }
                    cur = 0;
                }
            }
        }
    }

    for (let c = 0; c < n; c++) {
        const col = [];
        for (let r = 0; r < n; r++) col.push(grid[r][c]);
        const clue = clues.cols[c] || [0];
        const sum = clueSum(clue);
        const fills = col.filter((v) => v === CELL.FILLED).length;
        const maxRun = maxClueRun(clue);
        const long = longestFillRun(col);

        if (isZeroClue(clue)) {
            col.forEach((v, r) => {
                if (v === CELL.FILLED) add(r, c);
            });
            continue;
        }
        if (fills > sum) {
            col.forEach((v, r) => {
                if (v === CELL.FILLED) add(r, c);
            });
        }
        if (maxRun > 0 && long > maxRun) {
            let cur = 0;
            let start = 0;
            for (let r = 0; r <= n; r++) {
                const v = r < n ? col[r] : CELL.EMPTY;
                if (v === CELL.FILLED) {
                    if (cur === 0) start = r;
                    cur += 1;
                } else {
                    if (cur > maxRun) {
                        for (let y = start; y < start + cur; y++) add(y, c);
                    }
                    cur = 0;
                }
            }
        }
    }

    if (!candidates.length) {
        return { ok: false, error: 'NO_ERRORS', highlights: [] };
    }

    // Cap + shuffle so flooding the board never outlines the solution.
    shuffleInPlace(candidates);
    const highlights = candidates.slice(0, ERROR_DUST_MAX);
    return {
        ok: true,
        highlights,
        totalFound: candidates.length,
        capped: candidates.length > ERROR_DUST_MAX,
    };
}

/**
 * Apply one safe cell from the solution (DRAW fill or MARK vacant).
 * @returns {{ ok: boolean, highlights: Array<{r,c,kind}>, grid?: number[][], action?: 'DRAW'|'MARK', error?: string }}
 */
export function applyChalkTip(grid, solution) {
    if (!grid?.length || !solution?.length) {
        return { ok: false, error: 'NO_GRID', highlights: [] };
    }
    const n = grid.length;
    if (solution.length !== n || !Array.isArray(solution[0]) || solution[0].length !== n) {
        return { ok: false, error: 'NO_GRID', highlights: [] };
    }
    const candidates = [];
    for (let r = 0; r < n; r++) {
        for (let c = 0; c < n; c++) {
            if (grid[r][c] !== CELL.EMPTY) continue;
            const want = solution[r][c] === 1 ? CELL.FILLED : CELL.MARKED;
            candidates.push({
                r,
                c,
                kind: want === CELL.FILLED ? 'tip-fill' : 'tip-mark',
                want,
            });
        }
    }
    if (!candidates.length) {
        return { ok: false, error: 'NO_EMPTY', highlights: [] };
    }
    // Prefer a fill tip mid-board for usefulness
    candidates.sort((a, b) => {
        const score = (h) =>
            (h.kind === 'tip-fill' ? 0 : 10) +
            Math.abs(h.r - n / 2) +
            Math.abs(h.c - n / 2);
        return score(a) - score(b);
    });
    const pick = candidates[0];
    const next = grid.map((row) => [...row]);
    next[pick.r][pick.c] = pick.want;
    return {
        ok: true,
        highlights: [{ r: pick.r, c: pick.c, kind: pick.kind }],
        grid: next,
        action: pick.want === CELL.FILLED ? 'DRAW' : 'MARK',
    };
}

/** C1 — extend lock. Returns new expiry timestamp. */
export function applyLockOil(tile) {
    if (!tile || tile.status !== 'CLAIMED') {
        return { ok: false, error: 'NO_CLAIM' };
    }
    const base = Math.max(Date.now(), tile.lockExpiry || Date.now());
    const lockExpiry = base + LOCK_OIL_MS;
    return { ok: true, lockExpiry, addedMs: LOCK_OIL_MS };
}

/** Live claim-timer readout for UI (m:ss). */
export function formatLockCountdown(lockExpiry, now = Date.now()) {
    const ms = Math.max(0, (Number(lockExpiry) || 0) - now);
    const totalSec = Math.ceil(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return {
        ms,
        totalSec,
        label: `${m}:${String(s).padStart(2, '0')}`,
        urgent: totalSec > 0 && totalSec <= 60,
        critical: totalSec > 0 && totalSec <= 30,
        expired: totalSec <= 0,
    };
}

export { LOCK_OIL_MS };

/** C2 — +1 energy key. */
export function applySpareKey() {
    const before = getEnergyState();
    if (before.keys >= ENERGY_MAX) {
        return { ok: false, error: 'ENERGY_FULL', keys: before.keys };
    }
    const after = refundEnergyLocal();
    return { ok: true, keys: after.keys };
}

/** Debug / reseed helper */
export function resetAssistState() {
    localStorage.removeItem(STORAGE_KEY);
}
